import type { ShapeKind } from '@lumo/shared';
import type { Box, Vec } from '../math/geom.js';

export interface ShapeGeometry {
  /** Замкнутый контур в локальных координатах (0..w, 0..h). */
  outline: Vec[];
  /** Дополнительные незамкнутые линии (например, верх цилиндра). */
  extra: Vec[][];
  /** Область для текста. */
  textBox: Box;
}

const cache = new Map<string, ShapeGeometry>();

function arc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, steps: number): Vec[] {
  const out: Vec[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    out.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry });
  }
  return out;
}

function roundedRect(w: number, h: number, r: number): Vec[] {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  if (rr < 0.5) return [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
  const s = 6;
  return [
    ...arc(w - rr, rr, rr, rr, -Math.PI / 2, 0, s),
    ...arc(w - rr, h - rr, rr, rr, 0, Math.PI / 2, s),
    ...arc(rr, h - rr, rr, rr, Math.PI / 2, Math.PI, s),
    ...arc(rr, rr, rr, rr, Math.PI, Math.PI * 1.5, s),
  ];
}

function build(kind: ShapeKind, w: number, h: number): ShapeGeometry {
  const pad = Math.min(12, w * 0.08, h * 0.08);
  const full: Box = { x: pad, y: pad, w: Math.max(1, w - pad * 2), h: Math.max(1, h - pad * 2) };
  switch (kind) {
    case 'rect':
      return { outline: [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }], extra: [], textBox: full };
    case 'rounded':
      return { outline: roundedRect(w, h, Math.min(24, Math.min(w, h) * 0.2)), extra: [], textBox: full };
    case 'ellipse': {
      const steps = Math.max(32, Math.min(128, Math.round((w + h) / 6)));
      const k = Math.SQRT1_2;
      return {
        outline: arc(w / 2, h / 2, w / 2, h / 2, 0, Math.PI * 2, steps).slice(0, -1),
        extra: [],
        textBox: { x: (w * (1 - k)) / 2, y: (h * (1 - k)) / 2, w: w * k, h: h * k },
      };
    }
    case 'diamond':
      return {
        outline: [{ x: w / 2, y: 0 }, { x: w, y: h / 2 }, { x: w / 2, y: h }, { x: 0, y: h / 2 }],
        extra: [],
        textBox: { x: w * 0.25, y: h * 0.25, w: w * 0.5, h: h * 0.5 },
      };
    case 'triangle':
      return {
        outline: [{ x: w / 2, y: 0 }, { x: w, y: h }, { x: 0, y: h }],
        extra: [],
        textBox: { x: w * 0.25, y: h * 0.45, w: w * 0.5, h: h * 0.5 },
      };
    case 'star': {
      const pts: Vec[] = [];
      const cx = w / 2;
      const cy = h / 2 + h * 0.04;
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? 1 : 0.42;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push({ x: cx + Math.cos(a) * (w / 2) * r, y: cy + Math.sin(a) * (h / 2) * r * 1.05 });
      }
      return { outline: pts, extra: [], textBox: { x: w * 0.3, y: h * 0.38, w: w * 0.4, h: h * 0.34 } };
    }
    case 'hexagon': {
      const k = Math.min(w * 0.25, h * 0.5);
      return {
        outline: [{ x: k, y: 0 }, { x: w - k, y: 0 }, { x: w, y: h / 2 }, { x: w - k, y: h }, { x: k, y: h }, { x: 0, y: h / 2 }],
        extra: [],
        textBox: { x: k * 0.6, y: pad, w: w - k * 1.2, h: h - pad * 2 },
      };
    }
    case 'arrow': {
      const head = Math.min(w * 0.45, h * 0.9);
      const body = h * 0.25;
      return {
        outline: [
          { x: 0, y: body },
          { x: w - head, y: body },
          { x: w - head, y: 0 },
          { x: w, y: h / 2 },
          { x: w - head, y: h },
          { x: w - head, y: h - body },
          { x: 0, y: h - body },
        ],
        extra: [],
        textBox: { x: pad, y: body, w: Math.max(1, w - head * 0.6 - pad), h: h - body * 2 },
      };
    }
    case 'cloud': {
      // Контур из дуг вдоль эллипса.
      const bumps = 9;
      const pts: Vec[] = [];
      const cx = w / 2;
      const cy = h / 2;
      for (let i = 0; i < bumps; i++) {
        const a0 = (i / bumps) * Math.PI * 2;
        const a1 = ((i + 1) / bumps) * Math.PI * 2;
        const am = (a0 + a1) / 2;
        const p0 = { x: cx + Math.cos(a0) * w * 0.4, y: cy + Math.sin(a0) * h * 0.36 };
        const p1 = { x: cx + Math.cos(a1) * w * 0.4, y: cy + Math.sin(a1) * h * 0.36 };
        const bulge = { x: cx + Math.cos(am) * w * 0.52, y: cy + Math.sin(am) * h * 0.5 };
        for (let s = 0; s < 8; s++) {
          const t = s / 8;
          // Квадратичная Безье p0 → bulge → p1
          pts.push({
            x: (1 - t) * (1 - t) * p0.x + 2 * (1 - t) * t * bulge.x + t * t * p1.x,
            y: (1 - t) * (1 - t) * p0.y + 2 * (1 - t) * t * bulge.y + t * t * p1.y,
          });
        }
      }
      // Нормализуем в 0..w, 0..h
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of pts) {
        minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
      }
      const sx = w / (maxX - minX);
      const sy = h / (maxY - minY);
      return {
        outline: pts.map((p) => ({ x: (p.x - minX) * sx, y: (p.y - minY) * sy })),
        extra: [],
        textBox: { x: w * 0.2, y: h * 0.24, w: w * 0.6, h: h * 0.52 },
      };
    }
    case 'cylinder': {
      const ry = Math.min(h * 0.12, w * 0.25);
      const top = arc(w / 2, ry, w / 2, ry, Math.PI, Math.PI * 2, 24);
      const bottom = arc(w / 2, h - ry, w / 2, ry, 0, Math.PI, 24);
      return {
        outline: [...top, ...bottom],
        extra: [arc(w / 2, ry, w / 2, ry, 0, Math.PI, 24)],
        textBox: { x: pad, y: ry * 2 + pad * 0.5, w: w - pad * 2, h: Math.max(1, h - ry * 3 - pad) },
      };
    }
    case 'parallelogram': {
      const k = Math.min(w * 0.2, h * 0.6);
      return {
        outline: [{ x: k, y: 0 }, { x: w, y: 0 }, { x: w - k, y: h }, { x: 0, y: h }],
        extra: [],
        textBox: { x: k * 0.8, y: pad, w: w - k * 1.6, h: h - pad * 2 },
      };
    }
  }
}

export function shapeGeometry(kind: ShapeKind, w: number, h: number): ShapeGeometry {
  const key = `${kind}:${Math.round(w * 10)}:${Math.round(h * 10)}`;
  let g = cache.get(key);
  if (!g) {
    if (cache.size > 2000) cache.clear();
    g = build(kind, Math.max(1, w), Math.max(1, h));
    cache.set(key, g);
  }
  return g;
}

/** Разбивает замкнутый/открытый контур на штрихи для пунктира. */
export function dashPolyline(points: Vec[], closed: boolean, dash: number, gap: number): Vec[][] {
  const pts = closed && points.length ? [...points, points[0]!] : points;
  const out: Vec[][] = [];
  let drawing = true;
  let remain = dash;
  let current: Vec[] = pts.length ? [pts[0]!] : [];
  for (let i = 1; i < pts.length; i++) {
    let a = pts[i - 1]!;
    const b = pts[i]!;
    let segLen = Math.hypot(b.x - a.x, b.y - a.y);
    while (segLen > 0) {
      const step = Math.min(remain, segLen);
      const t = step / segLen;
      const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      if (drawing) current.push(p);
      segLen -= step;
      remain -= step;
      a = p;
      if (remain <= 1e-6) {
        if (drawing && current.length > 1) out.push(current);
        drawing = !drawing;
        remain = drawing ? dash : gap;
        current = drawing ? [p] : [];
      }
    }
  }
  if (drawing && current.length > 1) out.push(current);
  return out;
}
